/**
 * B2Ctelecom — product-mobile-media.js (fase 1, 2026-09)
 *
 * Doel: Shopify-gehoste productvideo's (media_type == 'video') volledig
 * uitsluiten uit de productgallery op mobiel (Dawn-breakpoint: <= 749px),
 * terwijl desktop (>= 750px) exact ongewijzigd blijft.
 *
 * Werking (bewust NIET alleen CSS):
 * - Slides met data-media-visibility="desktop-only" worden op mobiel via de
 *   Flickity-API (flickity.remove) uit de carousel gehaald en bij terugkeer
 *   naar desktop op hun oorspronkelijke, bij paginalaad vastgelegde index
 *   teruggeplaatst (flickity.insert). Flickity's cell-model, swipe-posities
 *   en selectie blijven daardoor correct: geen lege swipe-posities, geen
 *   ghost-cells, geen video-audio via de select-handler in global.js.
 * - Video-thumbnails worden op dezelfde manier verwijderd/teruggeplaatst en
 *   de door FlickityThumbnails (global.js) gecachte NodeList
 *   (element.carouselThumbnails) wordt ververst zodat de aria-current-
 *   markering op de juiste thumbnail blijft staan.
 * - Modal/lightbox-items met data-media-visibility="desktop-only" worden op
 *   mobiel eveneens fysiek uit de DOM verwijderd (dus ook niet bereikbaar
 *   via keyboard/focus of verborgen DOM-posities). Een MutationObserver
 *   vangt de innerHTML-hersync van de modal af die
 *   VariantSelects.updateMedia (global.js) bij variantwissel uitvoert; de
 *   CSS in product-media-gallery.liquid blijft als extra vangnet staan.
 * - Er wordt uitsluitend op matchMedia('(min-width: 750px)') 'change'
 *   gereageerd; geen window-resize-polling. Bij herhaald resizen over het
 *   breakpoint worden altijd dezelfde node-referenties hergebruikt: geen
 *   dubbele cells, thumbnails, IDs of event listeners.
 * - Elk onderdeel is in try/catch geisoleerd: als dit script faalt, blijft
 *   de bestaande gallery (afbeeldingen, swipe, varianten) gewoon werken.
 *
 * Fase 2 (nog NIET geimplementeerd): slides uit het metafield
 * custom.mobile_product_videos krijgen data-media-visibility="mobile-only".
 * Dit script behandelt die waarde nu al symmetrisch (verwijderd op desktop,
 * zichtbaar op mobiel); voor fase 2 is alleen extra Liquid-render nodig.
 */
(function () {
  'use strict';

  if (window.B2CProductMobileMedia) return;
  window.B2CProductMobileMedia = true;

  // Dawn-conventie: mobiel <= 749px / desktop >= 750px (zie .small-hide in base.css)
  var desktopMql = window.matchMedia('(min-width: 750px)');

  var VISIBILITIES = ['desktop-only', 'mobile-only'];
  var galleries = [];
  var modals = [];
  var modalObservers = [];

  function safe(fn) {
    try {
      fn();
    } catch (error) {
      if (window.console && console.warn) {
        console.warn('[product-mobile-media] genegeerde fout:', error);
      }
    }
  }

  function getFlickity(carouselEl) {
    if (!carouselEl) return null;
    if (carouselEl.flickity) return carouselEl.flickity;
    if (window.Flickity && typeof window.Flickity.data === 'function') {
      return window.Flickity.data(carouselEl) || null;
    }
    return null;
  }

  function pauseVideosIn(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('video').forEach(function (video) {
      try {
        video.pause();
      } catch (e) {
        /* noop */
      }
    });
  }

  function emptyGroups() {
    var groups = {};
    VISIBILITIES.forEach(function (key) {
      groups[key] = [];
    });
    return groups;
  }

  /* ------------------------------------------------------------------ *
   * Gallery (Flickity-slider + thumbnail-strip)
   * ------------------------------------------------------------------ */

  function collectGalleries() {
    galleries = [];

    document.querySelectorAll('media-gallery').forEach(function (galleryEl) {
      var carousel = galleryEl.querySelector('flickity-carousel');
      if (!carousel) return;

      var flickity = getFlickity(carousel);
      var allSlides = flickity && flickity.cells && flickity.cells.length
        ? flickity.cells.map(function (cell) { return cell.element; })
        : Array.prototype.slice.call(carousel.querySelectorAll('.product__media-item'));

      var gallery = {
        carousel: carousel,
        thumbs: galleryEl.querySelector('flickity-thumbnails'),
        slides: emptyGroups(),
        thumbItems: emptyGroups()
      };

      // Oorspronkelijke DOM-index bij paginalaad vastleggen (voorwaarde 3):
      // dit is de terugplaats-positie bij resize mobiel -> desktop.
      allSlides.forEach(function (slide, index) {
        var visibility = slide.getAttribute('data-media-visibility');
        if (gallery.slides[visibility]) {
          gallery.slides[visibility].push({ el: slide, index: index });
        }
      });

      if (gallery.thumbs) {
        var allThumbs = gallery.thumbs.querySelectorAll('.thumbnail-list__item');
        Array.prototype.forEach.call(allThumbs, function (thumb, index) {
          var visibility = thumb.getAttribute('data-media-visibility');
          if (gallery.thumbItems[visibility]) {
            gallery.thumbItems[visibility].push({ el: thumb, index: index });
          }
        });
      }

      var hasItems = VISIBILITIES.some(function (key) {
        return gallery.slides[key].length || gallery.thumbItems[key].length;
      });
      if (hasItems) galleries.push(gallery);
    });
  }

  function removeSlides(gallery, visibility) {
    var items = gallery.slides[visibility].filter(function (item) {
      return item.el.isConnected;
    });
    if (!items.length) return;

    items.forEach(function (item) {
      pauseVideosIn(item.el);
    });

    var flickity = getFlickity(gallery.carousel);
    if (flickity && flickity.isActive) {
      // Flickity haalt de cellen uit zijn cell-model EN uit de DOM;
      // aantallen, posities en selectie worden intern bijgewerkt.
      flickity.remove(items.map(function (item) { return item.el; }));
      flickity.resize();
    } else {
      items.forEach(function (item) {
        item.el.remove();
      });
    }
  }

  function restoreSlides(gallery, visibility) {
    var items = gallery.slides[visibility]
      .filter(function (item) { return !item.el.isConnected; })
      .sort(function (a, b) { return a.index - b.index; });
    if (!items.length) return;

    var flickity = getFlickity(gallery.carousel);
    if (flickity && flickity.isActive) {
      items.forEach(function (item) {
        // Oplopend op originele index invoegen zet elk item exact terug
        // op zijn oorspronkelijke positie.
        flickity.insert(item.el, Math.min(item.index, flickity.cells.length));
      });
      flickity.resize();
    } else {
      items.forEach(function (item) {
        var current = gallery.carousel.querySelectorAll('.product__media-item');
        var ref = current[item.index] || null;
        var parent = ref
          ? ref.parentNode
          : gallery.carousel.querySelector('.flickity-slider') || gallery.carousel;
        parent.insertBefore(item.el, ref);
      });
    }
  }

  function removeThumbs(gallery, visibility) {
    gallery.thumbItems[visibility].forEach(function (item) {
      if (item.el.isConnected) item.el.remove();
    });
  }

  function restoreThumbs(gallery, visibility) {
    if (!gallery.thumbs) return;
    gallery.thumbItems[visibility]
      .filter(function (item) { return !item.el.isConnected; })
      .sort(function (a, b) { return a.index - b.index; })
      .forEach(function (item) {
        var current = gallery.thumbs.querySelectorAll('.thumbnail-list__item');
        var ref = current[item.index] || gallery.thumbs.querySelector('.slider-button--next');
        gallery.thumbs.insertBefore(item.el, ref || null);
      });
  }

  function syncThumbnails(gallery) {
    if (!gallery.thumbs) return;

    // FlickityThumbnails (global.js) cachet de thumbnail-NodeList in de
    // constructor en gebruikt die positioneel voor de aria-current-markering.
    // Na het toevoegen/verwijderen van items moet die cache mee veranderen,
    // anders wijst selectedIndex naar de verkeerde thumbnail. De bestaande
    // click-listeners blijven op de (hergebruikte) nodes zelf staan; er wordt
    // dus niets opnieuw gebonden.
    gallery.thumbs.carouselThumbnails = gallery.thumbs.querySelectorAll('.slider__slide');

    var flickity = getFlickity(gallery.carousel);
    if (!flickity || !flickity.isActive || !flickity.cells.length) return;

    var selectedCell = flickity.cells[flickity.selectedIndex];
    var mediaId = selectedCell && selectedCell.element.getAttribute('data-media-id');
    if (!mediaId) return;

    var target = gallery.thumbs.querySelector(
      '.thumbnail-list__item[data-target="' + mediaId + '"] .thumbnail'
    );
    if (!target) return;

    gallery.thumbs.querySelectorAll('.thumbnail[aria-current]').forEach(function (el) {
      if (el !== target) el.removeAttribute('aria-current');
    });
    target.setAttribute('aria-current', 'true');
  }

  /* ------------------------------------------------------------------ *
   * Modal / lightbox
   * ------------------------------------------------------------------ */

  function collectModals() {
    modalObservers.forEach(function (observer) {
      observer.disconnect();
    });
    modalObservers = [];
    modals = [];

    document.querySelectorAll('.product-media-modal .product-media-modal__content').forEach(function (content) {
      var modal = { content: content, items: emptyGroups() };
      registerModalItems(modal);
      modals.push(modal);

      // VariantSelects.updateMedia (global.js) vervangt de modal-inhoud via
      // innerHTML bij variantwissel; dan verschijnen verse video-nodes en
      // vervallen onze referenties. De observer herbouwt de registratie
      // ALLEEN wanneer er niet-geregistreerde nodes opduiken (= zo'n swap);
      // onze eigen remove/restore werkt met bekende nodes en triggert dus
      // geen rebuild, zodat referenties voor terugplaatsen behouden blijven
      // en er geen loop kan ontstaan.
      var observer = new MutationObserver(function () {
        safe(function () {
          var tracked = new Set();
          VISIBILITIES.forEach(function (key) {
            modal.items[key].forEach(function (item) {
              tracked.add(item.el);
            });
          });

          var hasUnknown = false;
          VISIBILITIES.forEach(function (key) {
            content
              .querySelectorAll('[data-media-visibility="' + key + '"]')
              .forEach(function (el) {
                if (!tracked.has(el)) hasUnknown = true;
              });
          });

          if (hasUnknown) {
            registerModalItems(modal);
            applyModal(modal);
          }
        });
      });
      observer.observe(content, { childList: true });
      modalObservers.push(observer);
    });
  }

  function registerModalItems(modal) {
    modal.items = emptyGroups();
    var children = Array.prototype.slice.call(modal.content.children);
    children.forEach(function (child, index) {
      var visibility = child.getAttribute && child.getAttribute('data-media-visibility');
      if (modal.items[visibility]) {
        modal.items[visibility].push({ el: child, index: index });
      }
    });
  }

  function applyModal(modal) {
    var isDesktop = desktopMql.matches;

    VISIBILITIES.forEach(function (visibility) {
      var shouldShow = isDesktop
        ? visibility === 'desktop-only'
        : visibility === 'mobile-only';

      if (shouldShow) {
        modal.items[visibility]
          .filter(function (item) { return !item.el.isConnected; })
          .sort(function (a, b) { return a.index - b.index; })
          .forEach(function (item) {
            var ref = modal.content.children[item.index] || null;
            modal.content.insertBefore(item.el, ref);
          });
      } else {
        modal.items[visibility].forEach(function (item) {
          if (!item.el.isConnected) return;
          pauseVideosIn(item.el);
          item.el.remove();
        });
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Toepassen + init
   * ------------------------------------------------------------------ */

  function apply() {
    var isDesktop = desktopMql.matches;

    galleries.forEach(function (gallery) {
      safe(function () {
        if (isDesktop) {
          restoreSlides(gallery, 'desktop-only');
          removeSlides(gallery, 'mobile-only');
          restoreThumbs(gallery, 'desktop-only');
          removeThumbs(gallery, 'mobile-only');
        } else {
          removeSlides(gallery, 'desktop-only');
          restoreSlides(gallery, 'mobile-only');
          removeThumbs(gallery, 'desktop-only');
          restoreThumbs(gallery, 'mobile-only');
        }
        syncThumbnails(gallery);
      });
    });

    modals.forEach(function (modal) {
      safe(function () {
        applyModal(modal);
      });
    });
  }

  function init() {
    safe(collectGalleries);
    safe(collectModals);
    apply();
  }

  // Voorwaarde 5: alleen matchMedia 'change', geen resize-polling.
  if (desktopMql.addEventListener) {
    desktopMql.addEventListener('change', apply);
  } else if (desktopMql.addListener) {
    desktopMql.addListener(apply);
  }

  // Theme editor: sectie wordt opnieuw gerenderd -> registraties opnieuw opbouwen.
  document.addEventListener('shopify:section:load', function () {
    safe(init);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
